import { OpportunityFillOutIntakeForms } from '@/components/OpportunityFillOutIntakeForms';
import { OpportunityReviewResultsWithClient } from '@/components/OpportunityReviewResultsWithClient';
import {
  selectAdvisorOpportunitiesState,
  selectClients,
  selectClientsIsLoading,
  selectEligibleClientsForEngagement,
  selectPriorityStatusByEngagement,
  selectSessionAdvisor,
  selectSortedQualifyingOpportunityClients,
} from '@/model/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { postQualifyingOpportunityClientsRequest } from '@/model/advisorOpportunitiesModel';
import { useToast } from '@/components/hooks/use-toast';
import { OpportunityResendClientMagicLinks } from '@/components/OpportunityResendClientMagicLinks';
import { OpportunityClientCTA } from '@/components/OpportunityClientCTA';
import { OpportunityTryYourself } from '@/components/OpportunityTryYourself';
import { CirclePlay } from 'lucide-react';
import { PlayIcon } from '../components/PlayIcon';
import { OpportunityQaAClientROIProjection } from '@client/components/OpportunityQaAClientROIProjection';

export const DAYS_WITHOUT_REMINDER = 10;

type AdvisorDashboardOpportunitiesProps = {
  setShowInviteClientModal: (open: boolean) => void;
  setShowInviteClientModalWithPrepopulatedEmail: (open: boolean) => void;
};

export type OpportunityCtaButtonProps = {
  text: string;
  onClick: (personalizedMessageOrEvent?: string | React.MouseEvent) => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  warningDialog?: {
    title?: string;
    message: string;
  };
  engagementType?: ClientEmailEngagement;
};

export type PriorityStatus =
  | 'Highly recommended'
  | 'Recommended'
  | 'No action required'
  | 'Not applicable';

const priorityStatusOrder: Record<PriorityStatus, number> = {
  'Highly recommended': 3,
  'Recommended': 2,
  'No action required': 1,
  'Not applicable': 0,
};

type OpportunityComponents = {
  id: ClientEmailEngagement;
  component: React.ReactNode;
};
export interface OpportunityComponentProps {
  onPriorityChange: (priority: PriorityStatus) => void;
}

const OPPORTUNITIES_VIDEO_URL = 'https://www.loom.com/embed/0aa3420e34ee468ea2638d7f4874df8e';

export function AdvisorDashboardOpportunities({
  setShowInviteClientModal,
  setShowInviteClientModalWithPrepopulatedEmail,
}: AdvisorDashboardOpportunitiesProps) {
  const priorityStatus = useSelector(selectPriorityStatusByEngagement);

  const opportunities: OpportunityComponents[] = [
    {
      id: 'FILL_OUT_INTAKE_FORM',
      component: <OpportunityFillOutIntakeForms />,
    },
    {
      id: 'REVIEW_RESULTS_TOGETHER',
      component: <OpportunityReviewResultsWithClient />,
    },
    {
      id: 'RESEND_CLIENT_SIGNIN_LINKS',
      component: <OpportunityResendClientMagicLinks />,
    },
    {
      id: 'QAA_ROI_PROJECTION_LINKS',
      component: <OpportunityQaAClientROIProjection />,
    },
  ];
  const sortedOpportunities = [...opportunities].sort((a, b) => {
    const priorityA =
      priorityStatusOrder[(priorityStatus[a.id] as PriorityStatus) ?? 'Not applicable'];
    const priorityB =
      priorityStatusOrder[(priorityStatus[b.id] as PriorityStatus) ?? 'Not applicable'];
    return priorityB - priorityA;
  });

  return (
    <div>
      <main className="bg-primary-50 h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col items-center justify-between gap-8 md:flex-row md:gap-0">
          <div className="flex items-center gap-5">
            <h1 className="text-primary-dark text-4xl font-semibold">Opportunities</h1>
            <PlayIcon
              icon={CirclePlay}
              videoUrl={OPPORTUNITIES_VIDEO_URL}
            />
          </div>
        </div>
        <div className="bg-primary-100 my-6 h-px w-full" />
        <div className="flex flex-col items-center gap-4 overflow-x-auto md:flex-row md:gap-4">
          <OpportunityClientCTA setShowInviteClientModal={setShowInviteClientModal} />
          <OpportunityTryYourself
            setShowInviteClientModal={setShowInviteClientModalWithPrepopulatedEmail}
          />
          {sortedOpportunities.map(({ id, component }) => (
            <div key={id}>{component}</div>
          ))}
        </div>
      </main>
    </div>
  );
}

export const fetchQualifyingClients = (
  advisor: Advisor,
  engagementType: ClientEmailEngagement,
  dispatch: any,
) => {
  dispatch(
    postQualifyingOpportunityClientsRequest({
      advisorId: advisor.advisorId,
      engagementType,
    }),
  );
};

export function useHandleEmailSentStatus(
  state: AdvisorOpportunityState,
  onClose: () => void,
  engagementType: ClientEmailEngagement,
) {
  const { status, successfulEmails } = state;
  const emailType = engagementType === 'REVIEW_RESULTS_TOGETHER' ? 'request' : 'reminder';
  const plural = successfulEmails.length > 1;
  const dispatch = useDispatch();
  const { toast } = useToast();
  useEffect(() => {
    if (status === 'sendingEmailsComplete') {
      toast({
        title: `🎉 Success! ${successfulEmails.length} email${
          plural ? ` ${emailType}s` : ` ${emailType}`
        } sent.`,
        description: `Your ${plural ? 'clients' : 'client'} will receive their ${emailType}${
          plural ? 's' : ''
        } shortly and be one step closer to their goals.`,
        variant: 'default',
        className: 'bg-primary text-white -translate-y-5',
      });
      onClose();
    }
    if (status === 'sendingEmailsError') {
      toast({
        title: `Failed to send ${emailType} emails`,
        description: 'Please try again or contact support if the issue persists.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
    }
  }, [status, dispatch, toast, engagementType]);
}

export function determinePriorityStatus(
  qualifyingClients: Client[],
  clientsEligibleForReminder: Client[],
  highestPriorityStatus: PriorityStatus,
) {
  return clientsEligibleForReminder.length > 0
    ? highestPriorityStatus
    : qualifyingClients.length > 0
      ? 'No action required'
      : 'Not applicable';
}

export function useOpportunityState(engagementType: ClientEmailEngagement) {
  const advisor = useSelector(selectSessionAdvisor);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const dispatch = useDispatch();
  const advisorOpportunitiesState = useSelector(selectAdvisorOpportunitiesState)[engagementType];
  const priorityStatus = useSelector(selectPriorityStatusByEngagement)[engagementType];
  const qualifyingClients = useSelector(selectSortedQualifyingOpportunityClients)[engagementType];
  const clientsEligibleForReminder = useSelector(selectEligibleClientsForEngagement)[engagementType]
    .reminderEligible;
  const { status } = advisorOpportunitiesState;
  const [sheetOpen, setSheetOpen] = useState(false);

  return {
    advisor,
    dispatch,
    clientsIsLoading,
    status,
    priorityStatus,
    sheetOpen,
    setSheetOpen,
    qualifyingClients,
    advisorOpportunitiesState,
    clientsEligibleForReminder,
  };
}

export function useFetchQualifyingClients() {
  const advisor = useSelector(selectSessionAdvisor);
  const clients = useSelector(selectClients);
  const dispatch = useDispatch();
  const advisorOpportunitiesState = useSelector(selectAdvisorOpportunitiesState);

  // Fetch qualifying clients for each engagement type
  useEffect(() => {
    if (advisor) {
      const engagementTypes = Object.keys(advisorOpportunitiesState) as ClientEmailEngagement[];

      engagementTypes.forEach(engagementType => {
        fetchQualifyingClients(advisor, engagementType, dispatch);
      });
    }
  }, [advisor, dispatch, clients]);
}
